import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  Trash2, 
  Users,
  Plus,
  ArrowLeft,
  Loader2,
  CheckCircle,
  ShoppingCart,
  MapPin
} from "lucide-react";
import { useShakatahCartStore } from "@/stores/shakatahCartStore";
import { useToast } from "@/hooks/use-toast";
import { formatINR } from "@/lib/currency";
import { supabase } from "@/integrations/supabase/client";

// Unified product item for both cart and widget flows
interface CheckoutProduct {
  title: string;
  price: number;
  quantity: number;
  variantId: string | null;
  imageUrl: string | null;
  description: string | null;
}

// CRITICAL: Check markers SYNCHRONOUSLY before component mounts
// This prevents any flash of old cart data
// NOTE: Using hoisted function declaration to prevent TDZ errors in production builds
function checkCheckoutMarkers() {
  try {
    const checkoutInProgress = localStorage.getItem('shakatah-checkout-in-progress');
    const splitCompleted = localStorage.getItem('shakatah-split-completed');
    // Also check sessionStorage for same-session completion
    const sessionCompleted = sessionStorage.getItem('shakatah-checkout-completed-this-session');
    return !!(checkoutInProgress || splitCompleted || sessionCompleted);
  } catch {
    return false;
  }
}

// Check if this is a FRESH cart import from the widget
// If so, we should CLEAR old markers to allow the new cart
// NOTE: Using hoisted function declaration to prevent TDZ errors in production builds
function checkAndClearMarkersForFreshImport() {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const hasFreshCartData = urlParams.has('cartData') && urlParams.get('action') === 'cart';
    
    if (hasFreshCartData) {
      const hasMarkers = checkCheckoutMarkers();
      if (hasMarkers) {
        console.log('🟢 ShakatahCheckout: Fresh cart import detected - CLEARING OLD MARKERS');
        // Clear all checkout markers to allow the new cart
        localStorage.removeItem('shakatah-checkout-in-progress');
        localStorage.removeItem('shakatah-split-completed');
        localStorage.removeItem('shakatah-pending-session');
        localStorage.removeItem('shakatah-checkout-timestamp');
        sessionStorage.removeItem('shakatah-checkout-completed-this-session');
        sessionStorage.removeItem('shakatah-imported-cart-hash');
        return true; // Markers were cleared
      }
    }
    return false;
  } catch {
    return false;
  }
}

// NOTE: Previous module-level code removed to prevent production TDZ errors.
// Fresh import detection is now handled inside the component's useEffect.

// Aggressively clean URL of cart params using history API
// This removes cartData from browser history stack, not just current URL
// NOTE: Using hoisted function declaration to prevent TDZ errors in production builds
function cleanCartParamsFromHistory() {
  try {
    const url = new URL(window.location.href);
    const hadCartData = url.searchParams.has('cartData') || url.searchParams.has('action');
    
    if (hadCartData) {
      url.searchParams.delete('cartData');
      url.searchParams.delete('action');
      // Use replaceState to modify browser history entry itself
      window.history.replaceState({}, '', url.toString());
      console.log('Cleaned cart params from URL via replaceState');
    }
  } catch (e) {
    console.error('Error cleaning URL:', e);
  }
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
function ShakatahCheckout() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const { items, clearCart, addItem } = useShakatahCartStore();
  
  // Track if we've done the initial marker check
  const initialCheckDone = useRef(false);
  
  // Get store domain from URL parameter (passed by merchant's widget/embed)
  const storeDomain = searchParams.get('store') || '';
  const action = searchParams.get('action');
  const returnUrl = searchParams.get('returnUrl');
  const cartDataParam = searchParams.get('cartData');
  
  // Get product info from URL params (widget flow)
  const urlProductTitle = searchParams.get('title');
  const urlProductPrice = searchParams.get('price');
  const urlProductImage = searchParams.get('image');
  const urlVariantId = searchParams.get('variant');
  const urlProductHandle = searchParams.get('product');
  
  // Track if we've already added the item from URL
  const [itemAddedFromUrl, setItemAddedFromUrl] = useState(false);
  
  // State for dynamically loaded product data
  const [dynamicProductData, setDynamicProductData] = useState<{
    title: string;
    price: number;
    imageUrl: string | null;
  } | null>(null);
  const [loadingProduct, setLoadingProduct] = useState(false);
  
  // Determine if this is a widget flow (has URL product params) or cart flow
  const isWidgetFlow = !!(urlProductHandle || urlProductTitle);
  
  // CRITICAL: Check markers BEFORE any useState that might try to import
  // This uses the synchronous check defined outside the component
  const markersExist = checkCheckoutMarkers();
  
  // Track if we've already imported the widget cart
  const [widgetCartImported, setWidgetCartImported] = useState(() => {
    // Block import if checkout was completed (using pre-computed check)
    if (markersExist) {
      console.log('Checkout markers found on mount - blocking cart import');
      // Immediately clean URL to prevent back-button issues
      cleanCartParamsFromHistory();
      return true;
    }
    
    // Use a hash of the cart data to detect if it's already been imported
    if (cartDataParam) {
      try {
        const importedHash = sessionStorage.getItem('shakatah-imported-cart-hash');
        const currentHash = btoa(cartDataParam.substring(0, 100));
        if (importedHash === currentHash) {
          console.log('Cart data already imported in this session');
          return true;
        }
      } catch (e) {
        console.error('Error checking cart import hash:', e);
      }
    }
    return false;
  });
  
  // Force re-check when tab becomes visible (user returns from Shopify checkout)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && checkCheckoutMarkers()) {
        console.log('ShakatahCheckout: Tab visible with checkout markers - ensuring cart is blocked');
        setWidgetCartImported(true);
        // Clean URL immediately
        cleanCartParamsFromHistory();
        // Force clear the local cart state
        clearCart();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [clearCart]);
  
  // Handle importing cart from URL params when action=cart
  useEffect(() => {
    if (action === 'cart' && storeDomain && !widgetCartImported) {
      // CRITICAL: Check ALL markers - this is the LAST LINE OF DEFENSE
      const checkoutInProgress = localStorage.getItem('shakatah-checkout-in-progress');
      const splitCompleted = localStorage.getItem('shakatah-split-completed');
      const sessionCompleted = sessionStorage.getItem('shakatah-checkout-completed-this-session');
      
      const hasAnyMarker = !!(checkoutInProgress || splitCompleted || sessionCompleted);
      
      // FRESH IMPORT DETECTION: If this is a fresh cart import with cartData,
      // clear old markers to allow the new cart to be imported
      if (hasAnyMarker && cartDataParam) {
        console.log('🟢 ShakatahCheckout: Fresh cart import detected - CLEARING OLD MARKERS');
        localStorage.removeItem('shakatah-checkout-in-progress');
        localStorage.removeItem('shakatah-split-completed');
        localStorage.removeItem('shakatah-pending-session');
        localStorage.removeItem('shakatah-checkout-timestamp');
        sessionStorage.removeItem('shakatah-checkout-completed-this-session');
        sessionStorage.removeItem('shakatah-imported-cart-hash');
        // Continue to import the new cart (don't return)
      } else if (hasAnyMarker) {
        // No fresh cart data, but markers exist - block import
        console.log('ShakatahCheckout: BLOCKING cart import - checkout markers detected:', {
          checkoutInProgress: !!checkoutInProgress,
          splitCompleted: !!splitCompleted,
          sessionCompleted: !!sessionCompleted
        });
        setWidgetCartImported(true);
        cleanCartParamsFromHistory();
        
        // CRITICAL: Also ensure cart is empty
        clearCart();
        return;
      }
      
      // Also check if Zustand store already has items (race condition protection)
      // If we already have items AND no markers, something is wrong - don't re-import
      if (items.length > 0) {
        console.log('ShakatahCheckout: Cart already has items, skipping URL import');
        setWidgetCartImported(true);
        cleanCartParamsFromHistory();
        return;
      }
      
      let widgetItems: Array<{ variantId: string; title: string; price: number; image: string; handle: string; quantity: number }> = [];
      
      // First try to get cart data from URL params (cross-domain safe)
      if (cartDataParam) {
        try {
          widgetItems = JSON.parse(cartDataParam);
          console.log('Importing cart from URL params:', widgetItems);
          
          // Mark this cart data as imported to prevent re-import
          const currentHash = btoa(cartDataParam.substring(0, 100));
          sessionStorage.setItem('shakatah-imported-cart-hash', currentHash);
        } catch (e) {
          console.error('Error parsing cart data from URL:', e);
        }
      }
      
      // Fallback: try localStorage (only works if same domain, unlikely)
      if (widgetItems.length === 0) {
        const widgetCartKey = `shakatah-split-cart-${storeDomain}`;
        try {
          const widgetCartData = localStorage.getItem(widgetCartKey);
          if (widgetCartData) {
            const parsed = JSON.parse(widgetCartData);
            widgetItems = parsed.items || [];
            console.log('Importing cart from localStorage:', widgetItems);
            localStorage.removeItem(widgetCartKey);
          }
        } catch (e) {
          console.error('Error importing widget cart from localStorage:', e);
        }
      }
      
      if (widgetItems.length > 0) {
        clearCart();
        
        widgetItems.forEach((item) => {
          const cartItem = {
            lineId: null,
            product: {
              node: {
                id: item.variantId,
                title: item.title || 'Product',
                description: '',
                handle: item.handle || '',
                priceRange: { minVariantPrice: { amount: String(item.price || 0), currencyCode: 'INR' } },
                images: { edges: item.image ? [{ node: { url: item.image, altText: item.title } }] : [] },
                variants: { edges: [] },
                options: [],
              }
            },
            variantId: item.variantId,
            variantTitle: 'Default',
            price: { amount: String(item.price || 0), currencyCode: 'INR' },
            quantity: item.quantity || 1,
            selectedOptions: [],
          };
          addItem(cartItem);
        });
        
        toast({
          title: "Cart loaded",
          description: `${widgetItems.length} item${widgetItems.length !== 1 ? 's' : ''} loaded from your split cart.`,
        });
      }
      
      setWidgetCartImported(true);
      cleanCartParamsFromHistory();
    }
  }, [action, storeDomain, widgetCartImported, addItem, toast, returnUrl, clearCart, cartDataParam, items.length]);
  
  // Handle adding item to cart when action=add
  useEffect(() => {
    if (action === 'add' && urlVariantId && !itemAddedFromUrl) {
      const price = urlProductPrice ? parseFloat(urlProductPrice) : 0;
      const title = urlProductTitle ? decodeURIComponent(urlProductTitle) : 'Product';
      const image = urlProductImage ? decodeURIComponent(urlProductImage) : null;
      
      // Create a minimal cart item structure
      const cartItem = {
        lineId: null,
        product: {
          node: {
            id: urlVariantId,
            title: title,
            description: '',
            handle: urlProductHandle || '',
            priceRange: { minVariantPrice: { amount: String(price), currencyCode: 'INR' } },
            images: { edges: image ? [{ node: { url: image, altText: title } }] : [] },
            variants: { edges: [] },
            options: [],
          }
        },
        variantId: urlVariantId,
        variantTitle: 'Default',
        price: { amount: String(price), currencyCode: 'INR' },
        quantity: 1,
        selectedOptions: [],
      };
      
      addItem(cartItem);
      setItemAddedFromUrl(true);
      
      toast({
        title: "Added to Split Cart",
        description: `${title} has been added. Add more products or proceed to checkout.`,
      });
      
      // Clean up URL params after adding
      const newParams = new URLSearchParams();
      newParams.set('store', storeDomain);
      if (returnUrl) newParams.set('returnUrl', returnUrl);
      setSearchParams(newParams, { replace: true });
    }
  }, [action, urlVariantId, itemAddedFromUrl, urlProductTitle, urlProductPrice, urlProductImage, urlProductHandle, storeDomain, returnUrl, addItem, toast, setSearchParams]);
  
  // Check if we need to fetch product data (price is 0 or missing)
  const needsProductFetch = isWidgetFlow && (!urlProductPrice || parseFloat(urlProductPrice) === 0) && !itemAddedFromUrl;
  
  // Fetch product data from merchant's store if needed
  useEffect(() => {
    const fetchProductFromStore = async () => {
      if (!needsProductFetch || !storeDomain || !urlProductHandle) return;
      
      setLoadingProduct(true);
      try {
        // Get merchant's storefront token from our backend
        const { data: merchant } = await supabase
          .from('merchants')
          .select('id')
          .eq('shopify_store_domain', storeDomain)
          .maybeSingle();
        
        if (!merchant) {
          console.error('Merchant not found for domain:', storeDomain);
          setLoadingProduct(false);
          return;
        }
        
        // Use an edge function to fetch product data from the merchant's store
        const response = await supabase.functions.invoke('get-product-data', {
          body: { 
            merchantId: merchant.id, 
            productHandle: urlProductHandle,
            variantId: urlVariantId 
          }
        });
        
        if (response.data && !response.error) {
          setDynamicProductData({
            title: response.data.title || urlProductTitle || 'Product',
            price: response.data.price || 0,
            imageUrl: response.data.image || urlProductImage || null,
          });
        }
      } catch (err) {
        console.error('Error fetching product data:', err);
      } finally {
        setLoadingProduct(false);
      }
    };
    
    fetchProductFromStore();
  }, [needsProductFetch, storeDomain, urlProductHandle, urlVariantId, urlProductTitle, urlProductImage]);
  
  // Build unified product list from either cart, URL params, or dynamically fetched data
  const getProductPrice = (): number => {
    if (dynamicProductData?.price) return dynamicProductData.price;
    if (urlProductPrice && parseFloat(urlProductPrice) > 0) return parseFloat(urlProductPrice);
    return 0;
  };
  
  const getProductTitle = (): string => {
    if (dynamicProductData?.title) return dynamicProductData.title;
    if (urlProductTitle) return decodeURIComponent(urlProductTitle);
    return 'Product';
  };
  
  const getProductImage = (): string | null => {
    if (dynamicProductData?.imageUrl) return dynamicProductData.imageUrl;
    if (urlProductImage) return decodeURIComponent(urlProductImage);
    return null;
  };
  
  const checkoutProducts: CheckoutProduct[] = isWidgetFlow
    ? [{
        title: getProductTitle(),
        price: getProductPrice(),
        quantity: 1,
        variantId: urlVariantId ? decodeURIComponent(urlVariantId) : null,
        imageUrl: getProductImage(),
        description: null,
      }]
    : items.map(item => ({
        title: item.product.node.title,
        price: parseFloat(item.price.amount) * item.quantity,
        quantity: item.quantity,
        variantId: item.variantId,
        imageUrl: item.product.node.images?.edges?.[0]?.node?.url || null,
        description: item.product.node.description || null,
      }));
  
  // Validate we have products and store domain
  useEffect(() => {
    if (!storeDomain) {
      toast({
        title: "Missing store information",
        description: "This checkout page requires a valid store context",
        variant: "destructive",
      });
    }
  }, [storeDomain, toast]);
  
  // Cart step - user sees cart first, then proceeds to Shopify for delivery address
  const [step, setStep] = useState<"cart" | "complete">("cart");
  const [isCreatingDraftOrder, setIsCreatingDraftOrder] = useState(false);
  
  // Default participant count for draft order (will be updated in ShakatahSplitSetup)
  const participantCount = 2;
  
  // Calculate total from cart items first, fallback to checkout products
  const cartTotal = items.reduce((sum, item) => sum + parseFloat(item.price.amount) * item.quantity, 0);
  const totalPrice = cartTotal > 0 ? cartTotal : checkoutProducts.reduce((sum, p) => sum + p.price, 0);
  
  // Check if we have products to checkout
  const hasProducts = checkoutProducts.length > 0 && totalPrice > 0;
  
  // Show loading state while fetching product data
  if (loadingProduct) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <Loader2 className="h-16 w-16 text-primary mx-auto animate-spin" />
          <h1 className="text-2xl font-bold">Loading product...</h1>
          <p className="text-muted-foreground">
            Fetching product information from the store
          </p>
        </div>
      </div>
    );
  }

  // Redirect if no products (cart flow only - widget flow shows error)
  if (!hasProducts && step !== "complete") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <Users className="h-16 w-16 text-muted-foreground mx-auto" />
          <h1 className="text-2xl font-bold">
            {isWidgetFlow ? "Invalid product information" : "Your Shakatah cart is empty"}
          </h1>
          <p className="text-muted-foreground">
            {isWidgetFlow 
              ? "The product information could not be loaded. Please try again from the store."
              : "Add some products to split payment with friends"
            }
          </p>
          <Button onClick={() => {
            if (returnUrl) {
              window.location.href = decodeURIComponent(returnUrl);
            } else if (storeDomain) {
              window.location.href = `https://${storeDomain}`;
            } else {
              navigate("/");
            }
          }}>Continue Shopping</Button>
        </div>
      </div>
    );
  }

  // Proceed from cart to split setup - creates draft order and goes to split setup page
  const handleProceedToCheckout = async () => {
    if (items.length === 0 && checkoutProducts.length === 0) {
      toast({
        title: "Cart is empty",
        description: "Please add items to your cart before proceeding",
        variant: "destructive",
      });
      return;
    }

    setIsCreatingDraftOrder(true);
    
    try {
      // Build line items array for ALL cart items
      const lineItems = items.length > 0 
        ? items.map(item => ({
            variantId: item.variantId,
            quantity: item.quantity,
            title: item.product.node.title,
            image: item.product.node.images?.edges?.[0]?.node?.url || '',
          }))
        : checkoutProducts
            .filter(p => p.variantId)
            .map(p => ({
              variantId: p.variantId!,
              quantity: p.quantity,
              title: p.title,
              image: p.imageUrl || '',
            }));
      
      if (lineItems.length === 0) {
        throw new Error("No product variants found");
      }

      // Create draft order on Shopify with ALL cart items
      const { data, error } = await supabase.functions.invoke('create-draft-order', {
        body: {
          storeDomain,
          lineItems,
          participantCount,
        },
      });

      if (error) throw error;

      if (data?.sessionToken) {
        // CRITICAL: Clear all checkout completion markers when starting a NEW checkout
        // This ensures previous checkout's markers don't block this new session
        localStorage.removeItem('shakatah-checkout-in-progress');
        localStorage.removeItem('shakatah-split-completed');
        sessionStorage.removeItem('shakatah-checkout-completed-this-session');
        sessionStorage.removeItem('shakatah-imported-cart-hash');
        
        // Store session info in localStorage
        localStorage.setItem('shakatah-pending-session', JSON.stringify({
          sessionToken: data.sessionToken,
          participantCount,
          returnTime: Date.now(),
        }));
        
        // Go directly to split setup page (NOT to Shopify checkout)
        // Address will be collected on our page, payment happens later per-participant
        navigate(`/shakatah-split?token=${data.sessionToken}&participants=${participantCount}`);
      } else {
        throw new Error("Failed to create checkout session");
      }
      
    } catch (error: any) {
      console.error("Error creating draft order:", error);
      toast({
        title: "Checkout failed",
        description: error.message || "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsCreatingDraftOrder(false);
    }
  };

  if (step === "complete") {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-4 max-w-md">
          <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <CheckCircle className="h-10 w-10 text-primary" />
          </div>
          <h1 className="text-2xl font-bold">Checkout Complete!</h1>
          <p className="text-muted-foreground">
            Your split payment has been set up successfully.
          </p>
          
          <div className="pt-4">
            <Button 
              variant="outline" 
              onClick={() => {
                clearCart();
                if (returnUrl) {
                  window.location.href = decodeURIComponent(returnUrl);
                } else if (storeDomain) {
                  window.location.href = `https://${storeDomain}`;
                } else {
                  navigate("/");
                }
              }}
              className="w-full"
            >
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card sticky top-0 z-10">
        <div className="container max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-semibold flex items-center gap-2">
                <ShoppingCart className="h-5 w-5 text-primary" />
                Split Cart
              </h1>
              <p className="text-sm text-muted-foreground">
                Review your items & proceed to checkout
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="container max-w-2xl mx-auto px-4 pb-8">
        {/* Order Summary with Cart Management */}
        <div className="mb-6 p-4 bg-card rounded-xl border mt-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium">Your Split Cart</h3>
            {returnUrl && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => window.location.href = decodeURIComponent(returnUrl)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add More Items
              </Button>
            )}
          </div>
          <div className="space-y-3">
            {items.length > 0 ? (
              // Show cart items with quantity controls
              items.map((item) => (
                <div key={item.variantId} className="flex gap-3 items-center">
                  {item.product.node.images?.edges?.[0]?.node && (
                    <img 
                      src={item.product.node.images.edges[0].node.url} 
                      alt={item.product.node.title}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                  )}
                  <div className="flex-1">
                    <span className="font-medium">{item.product.node.title}</span>
                    <p className="text-sm text-muted-foreground">{formatINR(item.price.amount)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        const { updateQuantity } = useShakatahCartStore.getState();
                        updateQuantity(item.variantId, item.quantity - 1);
                      }}
                    >
                      <span className="text-lg">-</span>
                    </Button>
                    <span className="w-8 text-center">{item.quantity}</span>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => {
                        const { updateQuantity } = useShakatahCartStore.getState();
                        updateQuantity(item.variantId, item.quantity + 1);
                      }}
                    >
                      <span className="text-lg">+</span>
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => {
                        const { removeItem } = useShakatahCartStore.getState();
                        removeItem(item.variantId);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            ) : checkoutProducts.length > 0 ? (
              // Fallback: Show widget-flow products (non-editable)
              checkoutProducts.map((product, index) => (
                <div key={index} className="flex gap-3">
                  {product.imageUrl && (
                    <img 
                      src={product.imageUrl} 
                      alt={product.title}
                      className="w-16 h-16 object-cover rounded-lg"
                    />
                  )}
                  <div className="flex-1 flex justify-between">
                    <div>
                      <span className="font-medium">{product.title}</span>
                      {product.quantity > 1 && (
                        <span className="text-muted-foreground text-sm"> × {product.quantity}</span>
                      )}
                    </div>
                    <span className="font-medium">{formatINR(product.price)}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-4 text-muted-foreground">
                <p>Your cart is empty</p>
                {returnUrl && (
                  <Button 
                    variant="link" 
                    onClick={() => window.location.href = decodeURIComponent(returnUrl)}
                    className="mt-2"
                  >
                    Go back to add products
                  </Button>
                )}
              </div>
            )}
            {(items.length > 0 || checkoutProducts.length > 0) && (
              <div className="border-t pt-2 flex justify-between text-sm">
                <span className="text-muted-foreground">Subtotal (before shipping & tax)</span>
                <span className="font-medium">{formatINR(items.length > 0 ? items.reduce((sum, item) => sum + parseFloat(item.price.amount) * item.quantity, 0) : totalPrice)}</span>
              </div>
            )}
          </div>
        </div>

        {/* Cart Step - Review items and proceed to delivery address entry */}
        <div className="space-y-6">
          {/* Info card explaining the flow */}
          <div className="p-4 bg-primary/5 rounded-xl border border-primary/20">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">Split Payment with Friends</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose how many friends to split with, add their details, and everyone pays their share directly to the merchant.
                </p>
              </div>
            </div>
          </div>

          {/* Subtotal Preview */}
          <div className="p-4 bg-accent/50 rounded-xl border border-dashed">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-muted-foreground">Product Subtotal</span>
              <span className="font-semibold">{formatINR(totalPrice)}</span>
            </div>
            <div className="text-xs text-muted-foreground">
              Taxes included. Shipping calculated at checkout.
            </div>
          </div>

          {/* Add More Products Button */}
          {returnUrl && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => window.location.href = decodeURIComponent(returnUrl)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add More Products
            </Button>
          )}

          {/* Proceed Button */}
          <Button
            className="w-full"
            size="lg"
            onClick={handleProceedToCheckout}
            disabled={isCreatingDraftOrder || (items.length === 0 && checkoutProducts.length === 0)}
          >
            {isCreatingDraftOrder ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Reserving product...
              </>
            ) : (
              <>
                <MapPin className="w-4 h-4 mr-2" />
                Continue to Enter Delivery Address
              </>
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            Next: Enter your delivery address to calculate shipping & taxes
          </p>
        </div>
      </div>
    </div>
  );
}

export default ShakatahCheckout;
